'use strict';

import { USE_PIPELINE_STATUS_API_MOCKS } from '@/config/env.config';
import {
	getMockPipelineStatusSnapshotForScenario,
	requirePipelineStatusMockScenarioFromEnv,
} from '@/features/pipelineStatus/mocks/pipelineStatusFacts.mock';

/** @param {unknown} args @returns {string} */
function requestUrl(args) {
	if (typeof args === 'string') return args;
	if (args && typeof args === 'object' && typeof args.url === 'string') return args.url;
	return '';
}

/** @param {string} url */
function normalizePath(url) {
	let path = requestUrl(url);
	const qi = path.indexOf('?');
	const query = qi >= 0 ? path.slice(qi + 1) : '';
	if (qi >= 0) path = path.slice(0, qi);
	if (path.startsWith('/')) path = path.slice(1);
	path = path.replace(/^pipeline-tracker\//, 'pipeline-status/');
	return { path, query };
}

/** @param {string} query @param {string} key @param {number} defaultVal */
function intQueryParam(query, key, defaultVal) {
	const sp = new URLSearchParams(query);
	const raw = sp.get(key);
	if (raw == null || raw === '') return defaultVal;
	const n = parseInt(raw, 10);
	return Number.isFinite(n) ? n : defaultVal;
}

/**
 * @param {Record<string, unknown>[]} rows
 * @param {string | undefined} projectId
 */
function withProjectSubjectIds(rows, projectId) {
	if (!projectId) return rows;
	return rows.map((r) => {
		if (!r || typeof r !== 'object') return r;
		const row = /** @type {Record<string, unknown>} */ (r);
		if (String(row.scope ?? '') === 'Project') {
			return { ...row, subjectId: projectId };
		}
		return { ...row };
	});
}

/** @param {string} occurredAt */
function occurredMs(occurredAt) {
	const t = new Date(String(occurredAt || '')).getTime();
	return Number.isNaN(t) ? 0 : t;
}

/**
 * @param {import('@reduxjs/toolkit/query').BaseQueryApi | undefined} api
 * @param {string | undefined} projectId
 * @returns {unknown}
 */
function tryReadProjectPipelineFromPortalCache(api, projectId) {
	if (!api || typeof api.getState !== 'function' || !projectId) return undefined;
	try {
		// Lazy require avoids static circular dependency: portal/index.api → baseQueryWithAuth → this module → portal/index.api
		const { portalApi } = require('@/redux/services/portal/index.api');
		const slice = portalApi.endpoints.projectId.select(projectId)(api.getState());
		const data = slice?.data;
		if (!data || typeof data !== 'object') return undefined;
		return /** @type {Record<string, unknown>} */ (data).pipeline;
	} catch {
		return undefined;
	}
}

/**
 * When {@link USE_PIPELINE_STATUS_API_MOCKS} is true, returns a fetchBaseQuery-shaped `{ data }`
 * for Portal RTK requests under `pipeline-status/*` (and legacy `pipeline-tracker/*`), so no HTTP is sent.
 * @param {unknown} args RTK `query` return value (or string url)
 * @param {import('@reduxjs/toolkit/query').BaseQueryApi | undefined} api RTK baseQuery api (used to align mock facts with cached `projectId` payload when env scenario is unset)
 * @returns {Promise<{ data: unknown } | null>}
 */
export async function maybePortalPipelineStatusMockResponse(args, api) {
	if (!USE_PIPELINE_STATUS_API_MOCKS) return null;
	const method = (args && typeof args === 'object' && args.method) || 'GET';
	if (String(method).toUpperCase() !== 'GET') return null;

	const { path, query } = normalizePath(args);
	if (!path.startsWith('pipeline-status/')) return null;

	const factsMatch = /^pipeline-status\/projects\/([^/]+)\/facts$/.exec(path);
	const summaryMatch = !factsMatch && /^pipeline-status\/projects\/([^/]+)$/.exec(path);
	const projectIdFromPath = factsMatch?.[1] ?? summaryMatch?.[1];
	const projectPipeline = tryReadProjectPipelineFromPortalCache(api, projectIdFromPath);
	const snapshot = getMockPipelineStatusSnapshotForScenario(
		requirePipelineStatusMockScenarioFromEnv(projectPipeline),
	);
	const baseRows = snapshot.facts;
	const baseManifests = snapshot.iterationManifests;

	// GET pipeline-status/activity?…
	if (path === 'pipeline-status/activity') {
		const take = intQueryParam(query, 'take', 20);
		const sorted = [...baseRows].sort((a, b) => occurredMs(b.occurredAt) - occurredMs(a.occurredAt));
		return { data: sorted.slice(0, Math.max(0, take)) };
	}

	// GET pipeline-status/records?…
	if (path === 'pipeline-status/records') {
		const skip = intQueryParam(query, 'skip', 0);
		const pageSize = intQueryParam(query, 'page_size', 100);
		const slice = baseRows.slice(skip, skip + pageSize);
		return {
			data: {
				items: slice,
				totalCount: baseRows.length,
				skip,
				take: pageSize,
			},
		};
	}

	// GET pipeline-status/projects/{projectId}/facts
	if (factsMatch) {
		const projectId = factsMatch[1];
		return {
			data: {
				facts: withProjectSubjectIds(baseRows, projectId),
				iterationManifests: withProjectSubjectIds(baseManifests, projectId),
			},
		};
	}

	// GET pipeline-status/projects/{projectId} — summary DTO list (minimal mock)
	if (summaryMatch) {
		return { data: [] };
	}

	return null;
}
